import type { Components } from 'react-markdown';
import Markdown from 'react-markdown';
import remarkDirective from 'remark-directive';
import remarkGfm from 'remark-gfm';
import { defaultLinkColor, focusRingClassNames } from '@/components/ui/utils';
import { cn } from '@/lib/utils';

import { CmsMarkdownDirectiveHost } from './cmsComponentRegistry';
import { remarkCmsComponentDirective } from './remarkCmsComponentDirective';

export { CMS_COMPONENT_MARKDOWN_CHEAT_SHEET } from './remarkCmsComponentDirective';

const cmsInlineLinkClassName = cn(
    'underline underline-offset-4 hover:underline',
    `outline-none rounded-sm ${focusRingClassNames}`,
    defaultLinkColor,
);

/**
 * Raw HTML from Strapi (e.g. `<a>` inside rich text) may not use the markdown `components.a` mapper;
 * descendant selectors ensure those anchors still match theme link colors.
 */
const cmsMarkdownAnchorScopeClassName = cn(
    '[&_a]:underline [&_a]:underline-offset-4 [&_a]:hover:underline',
    '[&_a]:outline-none [&_a]:rounded-sm [&_a]:focus-visible:outline-2 [&_a]:focus-visible:outline-offset-2 [&_a]:focus-visible:outline-solid [&_a]:focus-visible:outline-focus-ring',
    'light:[&_a]:text-foreground light:[&_a]:hover:text-foreground light:[&_a]:focus-visible:text-foreground',
    'dark:[&_a]:text-foreground dark:[&_a]:hover:text-accent dark:[&_a]:focus-visible:text-accent',
);

/** Drop hast `node` from react-markdown props so it is not forwarded to DOM elements. */
function omitHastNode<P>(props: P): Omit<P, 'node'> {
    const { node, ...rest } = props as P & { node?: unknown };
    void node;
    return rest as Omit<P, 'node'>;
}

const markdownComponents = {
    'h1': (props) => {
        const { className, ...rest } = omitHastNode(props);
        return (
            <h1
                className={cn('scroll-m-20 type-headline-medium mt-8 mb-4 first:mt-0', className)}
                {...rest}
            />
        );
    },
    'h2': (props) => {
        const { className, ...rest } = omitHastNode(props);
        return (
            <h2
                className={cn('scroll-m-20 type-headline-small mt-8 mb-3 first:mt-0', className)}
                {...rest}
            />
        );
    },
    'h3': (props) => {
        const { className, ...rest } = omitHastNode(props);
        return <h3 className={cn('type-subheading-large mt-6 mb-2 first:mt-0', className)} {...rest} />;
    },
    'h4': (props) => {
        const { className, ...rest } = omitHastNode(props);
        return <h4 className={cn('type-subheading-medium mt-4 mb-2', className)} {...rest} />;
    },
    'p': (props) => {
        const { className, ...rest } = omitHastNode(props);
        return <p className={cn('type-body-default mb-4 last:mb-0', className)} {...rest} />;
    },
    'a': (props) => {
        const { className, ...rest } = omitHastNode(props);
        return (
            <a
                className={cn(cmsInlineLinkClassName, className)}
                {...rest}
            />
        );
    },
    'ul': (props) => {
        const { className, ...rest } = omitHastNode(props);
        return <ul className={cn('type-body-default list-disc pl-6 mb-4 space-y-2', className)} {...rest} />;
    },
    'ol': (props) => {
        const { className, ...rest } = omitHastNode(props);
        return <ol className={cn('type-body-default list-decimal pl-6 mb-4 space-y-2', className)} {...rest} />;
    },
    'li': (props) => {
        const { className, ...rest } = omitHastNode(props);
        return <li className={cn('type-body-default', className)} {...rest} />;
    },
    'blockquote': (props) => {
        const { className, ...rest } = omitHastNode(props);
        return (
            <blockquote
                className={cn('border-l-4 border-border pl-4 my-4 text-muted-foreground type-body-default', className)}
                {...rest}
            />
        );
    },
    'hr': (props) => {
        const { className, ...rest } = omitHastNode(props);
        return <hr className={cn('my-8 border-border', className)} {...rest} />;
    },
    'table': (props) => {
        const rest = omitHastNode(props);
        return (
            <div className="overflow-x-auto my-4">
                <table className={cn('w-full border-collapse border border-border text-sm')} {...rest} />
            </div>
        );
    },
    'thead': (props) => {
        const { className, ...rest } = omitHastNode(props);
        return <thead className={cn('bg-muted', className)} {...rest} />;
    },
    'th': (props) => {
        const { className, ...rest } = omitHastNode(props);
        return <th className={cn('border border-border px-3 py-2 text-left font-medium', className)} {...rest} />;
    },
    'td': (props) => {
        const { className, ...rest } = omitHastNode(props);
        return <td className={cn('border border-border px-3 py-2 align-top', className)} {...rest} />;
    },
    'img': (props) => {
        const { className, alt, ...rest } = omitHastNode(props);
        return (
            <span className="mx-auto my-4 block w-fit max-w-full overflow-hidden rounded-3xl border border-border">
                <img className={cn('block h-auto w-auto max-w-full', className)} alt={alt ?? ''} {...rest} />
            </span>
        );
    },
    'pre': (props) => {
        const { className, ...rest } = omitHastNode(props);
        return (
            <pre
                className={cn('overflow-x-auto rounded-md border border-border bg-muted p-4 my-4 text-sm', className)}
                {...rest}
            />
        );
    },
    'code': (props) => {
        const { className, children, ...rest } = omitHastNode(props);
        const isBlock = /language-[\w-]+/.test(className ?? '');
        if (isBlock) {
            return (
                <code className={cn('font-mono text-sm', className)} {...rest}>
                    {children}
                </code>
            );
        }
        return (
            <code className={cn('rounded bg-muted px-1 py-0.5 font-mono text-sm', className)} {...rest}>
                {children}
            </code>
        );
    },
    'strong': (props) => {
        const { className, ...rest } = omitHastNode(props);
        return <strong className={cn('font-semibold', className)} {...rest} />;
    },
    'cms-component': CmsMarkdownDirectiveHost,
} as Components;

type CmsMarkdownContentProps = {
    /** Markdown source from Strapi (body / description / content). */
    markdown: string;
    className?: string;
};

/**
 * Renders Strapi CMS markdown with GFM (tables, strikethrough, task lists, autolink literals)
 * and `::component{…}` leaf directives (see `remarkCmsComponentDirective`).
 */
export function CmsMarkdownContent({ markdown, className }: CmsMarkdownContentProps) {
    return (
        <div
            className={cn(
                'cms-markdown min-w-0 w-full break-words',
                cmsMarkdownAnchorScopeClassName,
                className,
            )}
        >
            <Markdown
                remarkPlugins={[remarkDirective, remarkGfm, remarkCmsComponentDirective]}
                components={markdownComponents}
            >
                {markdown}
            </Markdown>
        </div>
    );
}
